import React, { ReactElement, useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import Grid from '@material-ui/core/Grid';

import api from '~/services/api';
import Checkbox from '~/ui/components/inputs/Checkbox';
import Button from '~/ui/components/common/Button';
import Select from '~/ui/components/inputs/SelectWithoutAnimation';
import Input from '~/ui/components/inputs/Input';
import DatePicker from '~/ui/components/inputs/DatePicker';
import TimePicker from '~/ui/components/inputs/TimePicker';
import Loader from '~/ui/components/common/Loader';
import buildDate from '~/utils/date/buildDate';
import formatDictionaryOptions from '~/utils/formatDictionaryOptions';
import formatActTeamMemberOptions from '~/utils/formatActTeamMemberOptions';
import triggerNotificationOn from '~/ui/constants/triggerNotificationOn';
import { extractErrorMessage } from '~/utils/error/error';
import triggerFrequency from '~/ui/constants/triggerFrequency';
import triggerType from '~/ui/constants/triggerType';
import { useStoreActions } from '~/store/hooks';
import { triggerValidationSchema } from './validates';

import { IDictionaryTypes } from '~/services/api/dictionaries/types';
import { ITriggerInitialValues, IRequestInfo } from './types';
import { TriggerType } from '~/services/api/clientDetails/types';
import { IOption } from '~/types';
import { IDetails } from '../types';
import styles from './Styles.module.scss';

interface IProps {
  setModalTitle: (v: string) => void;
  setDetails: (v: IDetails) => void;
  defaultValues: ITriggerInitialValues;
  showSecondSubmit: boolean;
  details: IDetails | null;
  requestInfo: IRequestInfo;
}

interface IOptions {
  triggers: IOption[];
  teamMembers: IOption[];
}

const AddTrigger = ({
  setModalTitle,
  setDetails,
  defaultValues = { hasAlerts: false } as ITriggerInitialValues,
  showSecondSubmit,
  details,
  requestInfo,
}: IProps): ReactElement => {
  const [options, setOptions] = useState<IOptions>({} as IOptions);
  const [loading, setLoading] = useState(false);
  const {
    reset,
    unregister,
    register,
    control,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<ITriggerInitialValues>({
    defaultValues,
    resolver: triggerValidationSchema,
  });

  const { showError, showNotify } = useStoreActions(actions => actions.snackbar);
  const { onCreateTrigger, onUpdateTrigger } = useStoreActions(actions => actions.clientDetails);
  const setCurrent = useStoreActions(actions => actions.clientDetails.setCurrent);

  const hasAlerts = watch('hasAlerts');
  const isRecurring = watch('type') === TriggerType.Recurring;

  const onMount = useCallback(async () => {
    try {
      setLoading(true);
      const [triggers, teamMembers] = await Promise.all([
        api.dictionaries
          .getAvailableTypeList(IDictionaryTypes.Trigger)
          .then(r => formatDictionaryOptions(r.data)),
        api.actTeamMember
          .getActTeamMemberList(requestInfo.clinicId, requestInfo.teamId)
          .then(r => formatActTeamMemberOptions(r.data)),
      ]);

      setOptions({ triggers, teamMembers });
    } catch (e) {
      showError(extractErrorMessage(e));
    } finally {
      setLoading(false);
    }
  }, [requestInfo.clinicId, requestInfo.teamId, showError]);

  useEffect(() => {
    onMount();
  }, [onMount]);

  useEffect(() => {
    if (!hasAlerts) {
      unregister(['frequency', 'triggerDate', 'notificationOn', 'teamMembersToNotify', 'time']);
    }
  }, [hasAlerts, unregister]);

  const onSubmit = async (vals: ITriggerInitialValues, evt: React.BaseSyntheticEvent) => {
    const { name } = (evt as React.BaseSyntheticEvent<HTMLFormElement>).nativeEvent.submitter;

    try {
      let date: string;
      const { time, triggerDate, ...rest } = vals;

      if (time && triggerDate) {
        date = buildDate(time, triggerDate);
      }

      const requestPayload = date ? { ...rest, triggerDate: date } : rest;

      if (details && details.id) {
        const payload = {
          requestInfo: { ...requestInfo, id: String(details.id) },
          requestPayload,
        };

        await onUpdateTrigger(payload);
        setDetails(null);
        setCurrent(null);
      } else {
        const payload = {
          requestInfo,
          requestPayload,
        };

        await onCreateTrigger(payload);
        reset({ hasAlerts: false, note: '', triggerId: null, type: null });
      }

      setModalTitle(name);

      const type = details?.id ? 'updated' : 'added';

      showNotify({ message: `Trigger successfully ${type}` });
    } catch (e) {
      showError(extractErrorMessage(e));
    }
  };

  const filteredOptions = useMemo(
    () =>
      options?.teamMembers?.filter(
        option =>
          !option.isDisabled ||
          defaultValues?.teamMembersToNotify?.includes(option.value as number),
      ),
    [options, defaultValues],
  );

  if (loading) return <Loader />;

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Grid container spacing={2}>
        <Grid item sm={12}>
          <Select
            isRelativeWindow
            options={options.triggers}
            name="triggerId"
            control={control}
            errors={errors}
            label="Trigger"
            maxMenuHeight={200}
            isDisabled={!showSecondSubmit}
            hideSelectedOptions={false}
          />
        </Grid>
        <Grid item sm={12}>
          <Input name="note" register={register} label="Trigger note" multiline errors={errors} />
        </Grid>
        <Grid item sm={12}>
          <Select
            options={triggerType}
            name="type"
            control={control}
            errors={errors}
            label="Trigger type"
            hideSelectedOptions={false}
          />
        </Grid>
        {isRecurring && (
          <Grid item sm={12}>
            <Checkbox
              size="small"
              name="hasAlerts"
              control={control}
              errors={errors}
              label="Set notification for trigger alert"
            />
          </Grid>
        )}
        {hasAlerts && isRecurring && (
          <>
            <Grid item sm={6}>
              <Select
                options={triggerFrequency}
                name="frequency"
                control={control}
                errors={errors}
                label="Frequency"
                hideSelectedOptions={false}
              />
            </Grid>
            <Grid item sm={6}>
              <DatePicker
                name="triggerDate"
                control={control}
                openTo="date"
                errors={errors}
                label="Trigger date"
              />
            </Grid>
            <Grid item sm={6}>
              <Select
                options={triggerNotificationOn}
                name="notificationOn"
                control={control}
                errors={errors}
                label="Notification on"
                hideSelectedOptions={false}
              />
            </Grid>
            <Grid item sm={6}>
              <TimePicker name="time" control={control} label="Time" errors={errors} />
            </Grid>
            <Grid item sm={12}>
              <Select
                isMulti
                options={filteredOptions}
                name="teamMembersToNotify"
                control={control}
                label="Team member(s) to be notified"
                errors={errors}
                maxMenuHeight={150}
              />
            </Grid>
          </>
        )}
      </Grid>
      <div className={styles.buttonsWrapper}>
        <Button
          color="primary"
          variant="outlined"
          onClick={() => {
            setDetails(null);
            setModalTitle(null);
            setCurrent(null);
          }}
        >
          Cancel
        </Button>
        {showSecondSubmit && (
          <Button
            color="primary"
            variant="outlined"
            type="submit"
            name="Add Trigger"
            className={styles.margin}
          >
            Save and Add another
          </Button>
        )}
        <Button
          color="primary"
          variant="contained"
          type="submit"
          name={null}
          className={!showSecondSubmit ? styles.margin : ''}
        >
          {showSecondSubmit ? 'Save' : 'Update'}
        </Button>
      </div>
    </form>
  );
};

export default AddTrigger;
